import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatDialogRef } from '@angular/material/dialog';
import { MenuService } from 'src/app/services/menu/menu-service';
import { ModalInfoComponent } from 'src/app/shared/modal-info/modal-info.component';
import { ChartDataset, ChartOptions } from 'chart.js';
import * as moment from 'moment';
import { registerDashboardChartPlugin } from 'src/app/shared/dashboard/dashboard-chart-plugin';
import { AridosDashboardFacade } from './aridos-dashboard.facade';
import {
  AridosDashboardOrderDetails,
  AridosDashboardRow,
} from './aridos-dashboard.types';

registerDashboardChartPlugin();

@Component({
  selector: 'app-aridos-dashboard',
  templateUrl: './aridos-dashboard.component.html',
  styleUrls: ['./aridos-dashboard.component.css'],
  standalone: false,
})
export class AridosDashboardComponent implements OnInit, OnDestroy {
  ShowModalEncuestaMixer = false;
  isCollapsed = false;
  active = 'ss';

  loaded = false;
  showModal = false;
  fechaInicio = '';
  fechaFin = '';
  pedidoHoy: AridosDashboardRow | null = null;
  resumenDespacho: AridosDashboardRow[] = [];

  modalTitle = 'Actualiza tus estados';
  modalMessage = `
  Recuerda que el estado se mantendrá en tiempo
  <span class="estado-resaltado">“Activo”</span><br>
  por 2 horas. Si no hay actividad, cambiará a
  <span class="estado-resaltado">“Ausente”</span><br>
  y no recibirás pedidos.
`;

  guias_firmadas = 0;
  guias_no_firmadas = 0;
  guias_rechazadas = 0;
  pendientes = 0;
  entregados = 0;
  anulados = 0;
  totalVueltas = 0;

  firmadasChartData: ChartDataset<'doughnut', number[]>[] = [];
  firmadasChartLabels = ['Firmadas', 'No firmadas', 'Rechazadas'];
  firmadasChartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: true },
    },
  };
  chartLabels: string[] = [];
  chartData: ChartDataset<'bar', number[]>[] = [];

  dialogRef!: MatDialogRef<any>;
  isOnline = true;
  showOfflineToast = false;

  selectedTab = '';
  filteredGuias: AridosDashboardRow[] = [];
  selectedHistorialTab = '';
  filteredHistorial: AridosDashboardRow[] = [];

  @ViewChild('modalInfo', { static: true }) modalInfo!: ModalInfoComponent;

  private readonly destroy$ = new Subject<void>();
  private onlineSub?: Subscription;
  private offlineToastTimer: ReturnType<typeof setTimeout> | undefined;

  constructor(
    public readonly dashboardFacade: AridosDashboardFacade,
    public readonly menuService: MenuService,
  ) {
    menuService.btnPrevius = true;
    this.showModal = menuService.showModalFirstTime;
    menuService.showModalFirstTime = false;
  }

  ngOnInit(): void {
    this.isOnline = this.dashboardFacade.isOnline();
    this.onlineSub = this.dashboardFacade.online$
      .pipe(takeUntil(this.destroy$))
      .subscribe((online) => {
        this.isOnline = online;
      });

    this.loaded = true;
    this.fechaInicio = moment().startOf('month').format('YYYY-MM-DD');
    this.fechaFin = moment().endOf('month').format('YYYY-MM-DD');

    this.generarDashboard(this.fechaInicio, this.fechaFin);
    if (!this.hoyEnRangoSeleccionado()) {
      this.getDespachoDeHoy();
    }
    this.filtrarGuias();

    const modalInfoShow = sessionStorage.getItem('modalInfoShow');
    if (!modalInfoShow) {
      this.modalInfo.open();
      sessionStorage.setItem('modalInfoShow', 'true');
    }
  }

  onDateChange(): void {
    if (!this.fechaInicio || !this.fechaFin) return;

    this.generarDashboard(this.fechaInicio, this.fechaFin);
    if (!this.hoyEnRangoSeleccionado()) {
      this.getDespachoDeHoy();
    }
  }

  generarDashboard(fromDate: string, toDate: string): void {
    this.dashboardFacade
      .loadSummary(fromDate, toDate)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => this.applyResumenDespacho(data),
        error: (err) => console.error('Error al obtener resumen:', err),
      });
  }

  private applyResumenDespacho(data: AridosDashboardRow[]): void {
    this.resumenDespacho = data || [];
    this.guias_firmadas = this.resumenDespacho.filter(
      (item) => item.guideState === 'signed',
    ).length;
    this.guias_rechazadas = this.resumenDespacho.filter(
      (item) => item.guideState === 'rejected',
    ).length;
    this.guias_no_firmadas =
      this.resumenDespacho.length -
      this.guias_firmadas -
      this.guias_rechazadas;

    this.pendientes = this.resumenDespacho.filter(
      (item) => item.state === 'pendiente',
    ).length;
    this.entregados = this.resumenDespacho.filter(
      (item) => item.state === 'entregado',
    ).length;
    this.anulados = this.resumenDespacho.filter(
      (item) => item.state === 'anulado',
    ).length;

    const semanas = [
      ...new Set(this.resumenDespacho.map((item) => item.week)),
    ].sort();

    this.chartLabels = semanas.map((week) => `Semana ${week}`);
    const conteo = semanas.map(
      (week) => this.resumenDespacho.filter((item) => item.week === week).length,
    );

    this.chartData = [
      {
        data: conteo,
        label: 'Despachos por semana',
        backgroundColor: 'rgba(40,167,69,0.3)',
        borderColor: '#28a745',
        borderWidth: 1,
      },
    ];

    this.firmadasChartData = [
      {
        data: [
          this.guias_firmadas,
          this.guias_no_firmadas,
          this.guias_rechazadas,
        ],
        backgroundColor: ['#00B2E2', '#AC040A', '#ecb500ff'],
        borderColor: ['#00B2E2', '#AC040A', '#ecb500ff'],
        borderWidth: 2,
        label: 'Guías',
      },
    ];

    this.totalVueltas =
      this.guias_firmadas +
      this.guias_no_firmadas +
      this.guias_rechazadas;

    this.filtrarGuias();
    this.filtrarHistorial();

    if (this.hoyEnRangoSeleccionado()) {
      this.applyPedidoHoyFromResumen();
    }
  }

  private hoyEnRangoSeleccionado(): boolean {
    const hoy = moment().format('YYYY-MM-DD');
    return !!this.fechaInicio &&
      !!this.fechaFin &&
      this.fechaInicio <= hoy &&
      hoy <= this.fechaFin;
  }

  private applyPedidoHoyFromResumen(): void {
    const hoy = moment().format('YYYY-MM-DD');
    const deHoy = this.resumenDespacho.filter(
      (item) => item.deliveryDate === hoy,
    );
    this.setPedidoHoy(deHoy.length ? deHoy[0] : null);
  }

  private setPedidoHoy(pedido: AridosDashboardRow | null): void {
    this.pedidoHoy = pedido;
    if (!pedido) return;

    const tieneDetalle =
      pedido.product && pedido.quantity && pedido.deliveryTime;
    if (tieneDetalle) return;

    this.dashboardFacade
      .loadOrderDetails()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (orders: AridosDashboardOrderDetails[]) => {
          const order = orders.find(
            (item) =>
              item.orderNumber === pedido.orderNumber ||
              item.ticket === pedido.ticket,
          );
          if (!order) return;

          this.pedidoHoy = {
            ...pedido,
            product: pedido.product || order.product,
            quantity: pedido.quantity || order.quantity,
            deliveryTime: pedido.deliveryTime || order.deliveryTime,
          };
        },
        error: () => {
          // El resumen permanece visible aunque falte el detalle complementario.
        },
      });
  }

  getDespachoDeHoy(): void {
    const hoy = moment().format('YYYY-MM-DD');
    this.dashboardFacade
      .loadToday(hoy)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (row) => this.setPedidoHoy(row),
        error: (err) => console.error('Error al obtener despacho de hoy:', err),
      });
  }

  openModal(): void {
    this.showModal = true;
  }

  closeModal(): void {
    this.showModal = false;
  }

  getEstadoPlano(item: AridosDashboardRow | null): AridosDashboardRow['state'] {
    return item?.state ?? 'desconocido';
  }

  getEstadoClass(item: AridosDashboardRow | null): Record<string, boolean> {
    const state = this.getEstadoPlano(item);
    return {
      'is-pending': state === 'pendiente',
      'is-success': state === 'entregado',
      'is-danger': state === 'anulado',
    };
  }

  getEstadoStyle(item: AridosDashboardRow | null): Record<string, string> {
    const state = this.getEstadoPlano(item);
    if (state === 'pendiente') {
      return { borderColor: '#F59E0B', background: '#FFFBEB' };
    }
    if (state === 'entregado') {
      return { borderColor: '#22C55E', background: '#ECFDF5' };
    }
    if (state === 'anulado') {
      return { borderColor: '#EF4444', background: '#FEF2F2' };
    }
    return {};
  }

  setTab(tab: string): void {
    this.selectedTab = tab;
    this.filtrarGuias();
  }

  filtrarGuias(): void {
    if (!this.selectedTab) {
      this.filteredGuias = this.resumenDespacho;
      return;
    }

    this.filteredGuias = this.resumenDespacho.filter((row) => {
      if (this.selectedTab === 'firmadas') return row.guideState === 'signed';
      if (this.selectedTab === 'noFirmadas') return row.guideState === 'unsigned';
      if (this.selectedTab === 'rechazadas') return row.guideState === 'rejected';
      return true;
    });
  }

  setHistorialTab(tab: string): void {
    this.selectedHistorialTab = tab;
    this.filtrarHistorial();
  }

  filtrarHistorial(): void {
    if (!this.selectedHistorialTab) {
      this.filteredHistorial = this.resumenDespacho;
      return;
    }

    this.filteredHistorial = this.resumenDespacho.filter((row) => {
      if (this.selectedHistorialTab === 'pendientes') return row.state === 'pendiente';
      if (this.selectedHistorialTab === 'entregados') return row.state === 'entregado';
      if (this.selectedHistorialTab === 'anulados') return row.state === 'anulado';
      return true;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.onlineSub?.unsubscribe();
    if (this.offlineToastTimer) clearTimeout(this.offlineToastTimer);
  }

  onLockedNavClick(): void {
    if (this.offlineToastTimer) clearTimeout(this.offlineToastTimer);
    this.showOfflineToast = true;
    this.offlineToastTimer = setTimeout(
      () => (this.showOfflineToast = false),
      2500,
    );
  }

  toggleSidebar(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  setActive(section: string): void {
    this.active = section;
  }
}
